import * as _ from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

import actions from "@/actions";
import {
    markerColors,
    markerIcons,
    markerTypeLabels,
} from "@/constants/marker-constants";
import { Map as MapUtil } from "@/utils";

import ToolsBarContext from "../../ToolsBarContext";

const markerTypeOptions = [
    {
        markerType: "controller",
        label: markerTypeLabels.controller,
        ...markerColors.controller,
        icon: markerIcons.controller,
    },
    {
        markerType: "egw",
        label: markerTypeLabels.egw,
        ...markerColors.egw,
        icon: markerIcons.egw,
    },
    {
        markerType: "multiSensor",
        label: markerTypeLabels.multiSensor,
        ...markerColors.multiSensor,
        icon: markerIcons.multiSensor,
    },
    {
        markerType: "smartVue",
        label: markerTypeLabels.smartVue,
        ...markerColors.smartVue,
        icon: markerIcons.smartVue,
    },
];

const normalizePreviewSize = (length, width) => {
    const maxDimension = Math.max(width, length);
    let actualWidth;
    let actualLength;
    const desiredWidth = 3;
    const desiredHeight = 3;
    if (maxDimension === width) {
        actualWidth = desiredWidth;
        actualLength = (desiredHeight * length) / width;
    } else {
        actualWidth = (desiredWidth * width) / length;
        actualLength = desiredHeight;
    }
    return { actualLength, actualWidth };
};

/**
 * Panel for selecting a fixture type to add to the map.
 *
 * @returns The rendered add panel.
 */
const AddPanel = () => {
    const dispatch = useDispatch();
    const [filterValue, setFilterValue] = useState("");
    const allLightTypes = useSelector(({ map }) => map.general.lightTypes);

    const lightTypes = useMemo(
        () =>
            _.filter(
                allLightTypes,
                (o) =>
                    o.exteriorWidth &&
                    o.exteriorLength &&
                    _.includes(_.upperCase(o.displayName), filterValue),
            ),
        [allLightTypes, filterValue],
    );

    const renderedLightTypes = _.cloneDeep(lightTypes);

    if (renderedLightTypes) {
        for (let i = 0; i < renderedLightTypes.length; i++) {
            const newDimensions = normalizePreviewSize(
                renderedLightTypes[i].exteriorLength,
                renderedLightTypes[i].exteriorWidth,
            );
            renderedLightTypes[i].exteriorLength = newDimensions.actualLength;
            renderedLightTypes[i].exteriorWidth = newDimensions.actualWidth;
        }
    }

    const handleMarkerClick = (opt) =>
        dispatch(
            actions.map.general.changeMode("add", {
                markerType: opt.markerType,
            }),
        );

    return (
        <div className="add-wrapper">
            <div className="row header">
                <div className="col-11 title">Select a Fixture Type to Add</div>
                <ToolsBarContext.Consumer>
                    {(context) => (
                        <div
                            className="col-1 closebtn"
                            onClick={context.closePanel}
                        >
                            <span
                                aria-hidden="true"
                                style={{ cursor: "pointer" }}
                            >
                                &times;
                            </span>
                        </div>
                    )}
                </ToolsBarContext.Consumer>
            </div>
            <div className="row justify-content-center">
                <div className="col-11">
                    <input
                        className="form-control form-control-sm toolBarSearch"
                        type="text"
                        placeholder="Filter light list…"
                        onChange={(evt) =>
                            setFilterValue(evt.target.value.toUpperCase())
                        }
                    />
                </div>
            </div>
            <StyledOptionListContainer>
                <div className="listAddOption">
                    {_.map(lightTypes, (lightType, index) => (
                        <StyledOption
                            key={index}
                            onClick={() =>
                                dispatch(
                                    actions.map.general.changeMode("add", {
                                        lightType,
                                    }),
                                )
                            }
                        >
                            <StyledOptionImageContainer>
                                <StyledOptionFixtureSample
                                    $width={MapUtil.Converter.convertMeterToFeet(
                                        renderedLightTypes[index].exteriorWidth,
                                    )}
                                    $height={MapUtil.Converter.convertMeterToFeet(
                                        renderedLightTypes[index]
                                            .exteriorLength,
                                    )}
                                />
                            </StyledOptionImageContainer>
                            <StyledOptionDescription>
                                {lightType.displayName}
                            </StyledOptionDescription>
                        </StyledOption>
                    ))}
                    <StyledSectionHeader>Markers</StyledSectionHeader>
                    {markerTypeOptions.map((opt) => (
                        <StyledOption
                            key={opt.markerType}
                            onClick={() => handleMarkerClick(opt)}
                        >
                            <StyledOptionImageContainer>
                                <StyledMarkerPreview
                                    $fill={opt.fill}
                                    $stroke={opt.stroke}
                                >
                                    <StyledMarkerIcon
                                        src={opt.icon}
                                        alt={opt.label}
                                    />
                                </StyledMarkerPreview>
                            </StyledOptionImageContainer>
                            <StyledOptionDescription>
                                {opt.label}
                            </StyledOptionDescription>
                        </StyledOption>
                    ))}
                </div>
            </StyledOptionListContainer>
        </div>
    );
};

export default AddPanel;

const StyledOption = styled.div`
    height: 100px;
    width: 90px;
    padding-bottom: 0;
    text-align: center;
    cursor: pointer;
    padding: 3%;
    display: flex;
    flex-direction: column;
`;

const StyledOptionFixtureSample = styled.div`
    position: absolute;
    overflow: hidden;
    border: 1px solid #999999;
    background-color: #d6d6d6;
    width: ${(props) => props.$width * 5}px;
    height: ${(props) => props.$height * 5}px;
`;

const StyledOptionImageContainer = styled.div`
    position: relative;
    flex: 1;
    padding: 5px;
    display: flex;
    justify-content: center;
    align-items: center;
`;

const StyledOptionDescription = styled.div`
    font-size: 14px;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
`;

const StyledOptionListContainer = styled.div`
    flex: 1;
    overflow-y: auto;
    overflow-x: hidden;
`;

const StyledSectionHeader = styled.div`
    font-size: 12px;
    font-weight: 600;
    text-transform: uppercase;
    color: #666;
    padding: 8px 12px 4px;
    border-top: 1px solid #e0e0e0;
    margin-top: 4px;
    width: 100%;
`;

const StyledMarkerPreview = styled.div`
    width: 36px;
    height: 36px;
    border-radius: 50%;
    border: 2px solid ${(props) => props.$stroke};
    background-color: ${(props) => props.$fill};
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
`;

const StyledMarkerIcon = styled.img`
    width: 24px;
    height: 24px;
    object-fit: contain;
`;
